import { supabase } from '@/core/database/supabase';
import type { UserProfileUpdateInput, UserProfileDetail, UserDevice } from '../types/userProfile.types';

export const UserProfileService = {
  async getProfile(id: string): Promise<UserProfileDetail> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as UserProfileDetail;
  },

  async updateProfile(id: string, updates: UserProfileUpdateInput) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async uploadAvatar(userId: string, companyId: string, file: File): Promise<string> {
    const filePath = `media-assets/${companyId}/users/avatar-${userId}-${Date.now()}.webp`;

    const { error: uploadError } = await supabase.storage
      .from('media-assets')
      .upload(filePath, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('media-assets')
      .getPublicUrl(filePath);

    return publicUrl;
  },

  async getUserDevices(userId: string): Promise<UserDevice[]> {
    const { data, error } = await supabase
      .from('user_devices')
      .select('*')
      .eq('user_id', userId)
      .order('last_active', { ascending: false });

    if (error) throw error;
    return data as UserDevice[];
  },

  async logoutAllDevices(userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_devices')
      .delete()
      .eq('user_id', userId)
      .eq('is_current_session', false);

    if (error) throw error;
  },

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user?.email) throw new Error('Không thể xác thực người dùng');

    // Xác minh mật khẩu hiện tại bằng cách thử đăng nhập
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });

    if (signInError) {
      throw new Error('Mật khẩu hiện tại không chính xác');
    }

    // Cập nhật mật khẩu mới
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (updateError) {
      throw new Error('Lỗi khi cập nhật mật khẩu: ' + updateError.message);
    }
  }
};
